import clsx from 'clsx';
import Fuse from 'fuse.js';
import * as React from 'react';
import { atom, useRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    type FileUploadMetadata,
    isDrawingFile,
    isImageFile,
    isOpenAPIFile,
} from '@gitbook/app-database';
import { createFile, getLastEdit, type RevisionFile } from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIllustration,
    FilesDropZone,
    type FilesDropZoneAccept,
    Heading,
    List,
    type ListRenderItemInfo,
    ListSeparator,
    Pagination,
    ProgressiveImage,
    TextInput,
    usePaginatedList,
} from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { useLoggedInUser } from '../../state';
import { FileItem, type FileItemMode } from './FileItem';
import { type FilesSort, FilesSortButton, useFilesSort } from './FilesSortButton';
import { ImagesGrid } from './ImagesGrid';
import emptyImageDarkSrc from './images/empty-dark.svg';
import emptyImageLightSrc from './images/empty-light.svg';
import { useUploadFiles } from './useUploadFiles';

const logger = Logger('files-manager');

export interface FilesManagerProps {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;

    /** How to visually present files */
    mode?: FileItemMode;

    /** Type of files to show and accept on upload */
    accept?: FilesDropZoneAccept;

    /** If true, no edit actions are shown */
    readOnly?: boolean;

    /** Callback when selecting files */
    onSelect?: (files: api.ContentRefFile[]) => void;

    /** Class names to pass to the wrapper of the list */
    className?: string;
}

const filesQueryAtom = atom({
    key: 'filesQuery',
    default: '',
});

/**
 * In-space files manager.
 */
export function FilesManager(props: FilesManagerProps): React.ReactElement {
    const {
        space,
        changeRequest,
        accept,
        onSelect,
        mode = 'list',
        readOnly = false,
        className,
    } = props;

    // We use the source revision to avoid displaying computed files
    // as the user can't edit them.
    // We might want to display computed files in the future, as it's technically possible
    // for the user to insert them in a non computed page.
    const { sourceRevision: revision } = useHiveRevision();

    const [query, setQuery] = useRecoilState(filesQueryAtom);
    const [sortOptions, setSortOptions] = useFilesSort();
    const hiveBranch = useHiveBranch();
    const user = useLoggedInUser();
    const { uploadFiles } = useUploadFiles();

    /**
     * Handle updating the current revision in async once the files are uploaded.
     * The `beforeUpdate` function is called before the revision is updated and must return
     * the list of files that have been uploaded.
     */
    const uploadFilesToBranch = useEventCallback(
        async ({ select, files }: { select: boolean; files: FileUploadMetadata[] }) => {
            assertIsDefined(hiveBranch);

            const results = await hiveBranch.change((change) => {
                return files.map<api.ContentRefFile>((metadata) => {
                    const file = createFile(metadata, user.uid);

                    change.insertFile(file);

                    return { kind: 'file', file: file.id };
                });
            });

            analytics.logEvent(
                analytics.createSpaceEvent(space, {
                    type: 'edit_upload_files',
                    files: files.filter((metadata) => !isImageFile(metadata)).length,
                    images: files.filter((metadata) => isImageFile(metadata)).length,
                })
            );

            results.forEach((result, index) => {
                logger.info(`file ${result.file} uploaded (${index + 1} / ${results.length})`);
            });

            if (select) {
                onSelect?.(results);
            }
        }
    );

    /** List of all files in the revision */
    const files = React.useMemo(() => {
        const allFiles = Object.values(revision.files);

        if (accept === 'images') {
            return allFiles.filter((file) => isImageFile(file));
        }
        if (accept === 'openapi') {
            return allFiles.filter((file) => isOpenAPIFile(file));
        }
        if (accept === 'drawings') {
            return allFiles.filter((file) => isDrawingFile(file));
        }
        return allFiles;
    }, [revision.files, accept]);

    /** List files and filter with the query */
    const filteredFiles = React.useMemo(() => {
        const sorted = sortFiles(files, sortOptions);
        if (!query) {
            return sorted;
        }

        const fuse = new Fuse(sorted, {
            shouldSort: true,
            threshold: 0.6,
            distance: 100,
            minMatchCharLength: 1,
            keys: ['name'],
        });

        return fuse.search(query).map((x) => x.item);
    }, [files, query, sortOptions]);

    const list = usePaginatedList({
        label: 'files',
        items: filteredFiles,
        itemsPerPage: 15,
    });

    /** Callback to render a file in the list */
    const renderItem = React.useCallback(
        ({ item }: ListRenderItemInfo<RevisionFile>) => (
            <FileItem
                space={space}
                changeRequest={changeRequest}
                file={item}
                readOnly={readOnly}
                mode={mode}
                query={query}
                onPress={
                    onSelect
                        ? () => {
                              onSelect([{ kind: 'file', file: item.id }]);
                          }
                        : undefined
                }
            />
        ),
        [mode, space, changeRequest, readOnly, query, onSelect]
    );

    const onSelectFiles = async (nativeFiles: File[]) => {
        // reset the sort so that the uploaded files will show at the top
        setSortOptions({ by: 'updated', order: 'desc' });
        const files = await uploadFiles(nativeFiles, {
            accept,
        });
        await uploadFilesToBranch({
            select: true,
            files,
        });
    };

    if (files.length === 0) {
        return readOnly ? (
            <EmptyState>
                <EmptyStateIllustration>
                    <ProgressiveImage
                        width={65}
                        height={64}
                        src={{
                            light: emptyImageLightSrc,
                            dark: emptyImageDarkSrc,
                        }}
                    />
                </EmptyStateIllustration>
                <Heading>No files</Heading>
                <Description>No files have been uploaded to this space yet.</Description>
            </EmptyState>
        ) : (
            <>
                {onSelect ? (
                    <div className="flex flex-col items-stretch">
                        <FilesDropZone acceptedFileTypes={accept} onSelectFiles={onSelectFiles} />
                    </div>
                ) : (
                    <EmptyState>
                        <EmptyStateIllustration>
                            <ProgressiveImage
                                width={72}
                                height={64}
                                src={{
                                    light: emptyImageLightSrc,
                                    dark: emptyImageDarkSrc,
                                }}
                            />
                        </EmptyStateIllustration>
                        {/* <Heading>No files</Heading> */}
                        <Description>
                            Upload and store files in your space, and reference them across pages.
                        </Description>
                        <EmptyStateActions>
                            <FilesDropZone
                                icon={null}
                                acceptedFileTypes={accept}
                                onSelectFiles={onSelectFiles}
                            />
                        </EmptyStateActions>
                    </EmptyState>
                )}
            </>
        );
    }

    return (
        <div className={clsx('flex flex-1 flex-col gap-6', className)}>
            <div className="flex flex-col gap-4">
                <div className="flex gap-2">
                    <TextInput
                        leadingIcon={Icon.Search}
                        placeholder="Search files"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        autoFocus
                    />
                    <FilesSortButton />
                </div>
                {readOnly ? null : (
                    <FilesDropZone acceptedFileTypes={accept} onSelectFiles={onSelectFiles} />
                )}
            </div>

            {mode === 'grid' ? (
                <ImagesGrid>
                    {list.items.map((item, index) => (
                        <React.Fragment key={index}>{renderItem({ item, index })}</React.Fragment>
                    ))}
                </ImagesGrid>
            ) : (
                <List
                    ref={list.ref}
                    items={list.items}
                    renderItem={renderItem}
                    separator={ListSeparator}
                />
            )}

            <Pagination {...list.paginationProps} />
        </div>
    );
}

/** Sort files according to configured criteria */
function sortFiles(files: RevisionFile[], sort: FilesSort): RevisionFile[] {
    const results = [...files];
    const order = sort.order === 'desc' ? 1 : -1;

    results.sort((a, b) => {
        const aValue = getFileSortValue(a, sort);
        const bValue = getFileSortValue(b, sort);

        if (aValue < bValue) {
            return 1 * order;
        }
        if (aValue > bValue) {
            return -1 * order;
        }
        return 0;
    });

    return results;
}

/** Get value to use for sorting */
function getFileSortValue(file: RevisionFile, sort: FilesSort): number {
    if (sort.by === 'updated') {
        const last = getLastEdit(file.edits);
        if (!last) {
            return 0;
        }

        return last.updatedAt;
    }
    return file.size;
}
